import { ref, computed, onUnmounted } from 'vue'
import { voiceApi } from '@/api/voice'

interface VoiceResult {
  transcript: string
  intent?: string
  entities?: Record<string, string>
  confidence?: number
  language?: string
}

export function useVoice() {
  // State
  const isListening = ref(false)
  const isProcessing = ref(false)
  const transcript = ref('')
  const error = ref<string | null>(null)
  const audioChunks = ref<Blob[]>([])

  // Web Speech API support
  const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
  const isSupported = computed(() => !!SpeechRecognition || !!navigator.mediaDevices?.getUserMedia)

  let recognition: any = null
  let mediaRecorder: MediaRecorder | null = null
  let audioStream: MediaStream | null = null

  // Initialize speech recognition (browser native)
  function initSpeechRecognition() {
    if (!SpeechRecognition) return null

    const recognizer = new SpeechRecognition()
    recognizer.continuous = true
    recognizer.interimResults = true
    recognizer.lang = 'fr-FR' // Can be changed to 'ar-MA' for Darija

    recognizer.onresult = (event: any) => {
      let finalTranscript = ''
      let interimTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          finalTranscript += result[0].transcript
        } else {
          interimTranscript += result[0].transcript
        }
      }

      transcript.value = finalTranscript || interimTranscript
    }

    recognizer.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error)
      if (event.error === 'no-speech') {
        error.value = 'Aucune parole détectée'
      } else if (event.error === 'audio-capture') {
        error.value = 'Microphone non disponible'
      } else if (event.error === 'not-allowed') {
        error.value = 'Permission microphone refusée'
      } else {
        error.value = `Erreur: ${event.error}`
      }
      isListening.value = false
    }

    recognizer.onend = () => {
      if (isListening.value) {
        // Restart if still supposed to be listening
        recognizer.start()
      }
    }

    return recognizer
  }

  // Initialize audio recording for Voice Gateway
  async function initAudioRecording() {
    try {
      audioStream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaRecorder = new MediaRecorder(audioStream, {
        mimeType: 'audio/webm;codecs=opus'
      })

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.value.push(event.data)
        }
      }

      return mediaRecorder
    } catch (err) {
      console.error('Failed to init audio recording:', err)
      error.value = 'Impossible d\'accéder au microphone'
      return null
    }
  }

  // Start listening
  async function startListening(useVoiceGateway = false) {
    error.value = null
    transcript.value = ''
    audioChunks.value = []
    isListening.value = true

    if (useVoiceGateway) {
      // Use Voice Gateway for Darija support
      const recorder = await initAudioRecording()
      if (recorder) {
        recorder.start(100) // Collect data every 100ms
      }
    } else {
      // Use browser native speech recognition
      recognition = initSpeechRecognition()
      if (recognition) {
        try {
          recognition.start()
        } catch (err) {
          console.error('Failed to start recognition:', err)
          error.value = 'Erreur démarrage reconnaissance'
          isListening.value = false
        }
      } else {
        // Fallback to audio recording
        const recorder = await initAudioRecording()
        if (recorder) {
          recorder.start(100)
        }
      }
    }
  }

  // Stop listening and process
  async function stopListening(): Promise<VoiceResult | null> {
    isListening.value = false
    isProcessing.value = true

    try {
      if (recognition) {
        recognition.stop()
        recognition = null
      }

      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop()

        // Wait for final data
        await new Promise(resolve => setTimeout(resolve, 100))

        if (audioChunks.value.length > 0) {
          // Send to Voice Gateway for processing
          const audioBlob = new Blob(audioChunks.value, { type: 'audio/webm' })
          const result = await processAudioWithGateway(audioBlob)
          isProcessing.value = false
          return result
        }
      }

      // If we have transcript from browser recognition
      if (transcript.value) {
        // Optionally send to Voice Gateway for intent extraction
        const result = await extractIntent(transcript.value)
        isProcessing.value = false
        return result
      }

      isProcessing.value = false
      return null
    } catch (err) {
      console.error('Error stopping listening:', err)
      error.value = 'Erreur traitement audio'
      isProcessing.value = false
      return null
    } finally {
      // Clean up audio stream
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop())
        audioStream = null
      }
      mediaRecorder = null
    }
  }

  // Process audio with Voice Gateway (for Darija support)
  async function processAudioWithGateway(audioBlob: Blob): Promise<VoiceResult | null> {
    try {
      const response = await voiceApi.transcribe(audioBlob)
      transcript.value = response.transcript

      return {
        transcript: response.transcript,
        intent: response.intent,
        entities: response.entities,
        confidence: response.confidence,
        language: response.language,
      }
    } catch (err) {
      console.error('Voice Gateway error:', err)
      error.value = 'Erreur Voice Gateway'
      return null
    }
  }

  // Extract intent from text (using Voice Gateway)
  async function extractIntent(text: string): Promise<VoiceResult> {
    try {
      const response = await voiceApi.extractIntent(text)
      return {
        transcript: text,
        intent: response.intent,
        entities: response.entities,
        confidence: response.confidence,
      }
    } catch (err) {
      console.error('Intent extraction error:', err)
      // Return just the transcript if intent extraction fails
      return { transcript: text }
    }
  }

  // Text-to-speech
  async function speak(text: string, language = 'fr-FR') {
    // Try Voice Gateway TTS first
    try {
      const audioUrl = await voiceApi.textToSpeech(text, language)
      const audio = new Audio(audioUrl)
      await audio.play()
      return
    } catch (err) {
      console.warn('Voice Gateway TTS failed, falling back to browser:', err)
    }

    // Fallback to browser TTS
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = language
      utterance.rate = 0.9
      utterance.pitch = 1
      window.speechSynthesis.speak(utterance)
    }
  }

  // Cancel any ongoing speech
  function cancelSpeech() {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel()
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    if (recognition) {
      recognition.stop()
    }
    if (audioStream) {
      audioStream.getTracks().forEach(track => track.stop())
    }
    cancelSpeech()
  })

  return {
    // State
    isListening,
    isProcessing,
    transcript,
    error,
    isSupported,
    // Actions
    startListening,
    stopListening,
    speak,
    cancelSpeech,
    extractIntent,
  }
}

// Hook for voice commands
export function useVoiceCommands() {
  const { startListening, stopListening, speak, isListening, transcript, error, isSupported } = useVoice()

  const commands = ref<{ pattern: RegExp; handler: (matches: RegExpMatchArray) => void }[]>([])

  function registerCommand(pattern: RegExp, handler: (matches: RegExpMatchArray) => void) {
    commands.value.push({ pattern, handler })
  }

  async function processCommand(): Promise<boolean> {
    const result = await stopListening()
    if (!result?.transcript) return false

    const text = result.transcript.toLowerCase()

    for (const command of commands.value) {
      const matches = text.match(command.pattern)
      if (matches) {
        command.handler(matches)
        return true
      }
    }

    return false
  }

  // Pre-defined diabetes-related commands in French
  function registerDiabetesCommands(handlers: {
    onLogMeal?: (food?: string) => void
    onCheckGlucose?: () => void
    onLogActivity?: (activity?: string) => void
    onTakeMedication?: (medication?: string) => void
    onHelp?: () => void
  }) {
    if (handlers.onLogMeal) {
      registerCommand(/(?:j'ai mangé|repas|manger|enregistrer repas)(?:\s+(.+))?/i, (m) => {
        handlers.onLogMeal!(m[1])
      })
    }

    if (handlers.onCheckGlucose) {
      registerCommand(/(?:glycémie|glucose|ma glycémie|mon glucose|quel est mon)/i, () => {
        handlers.onCheckGlucose!()
      })
    }

    if (handlers.onLogActivity) {
      registerCommand(/(?:activité|sport|exercice|j'ai fait)(?:\s+(.+))?/i, (m) => {
        handlers.onLogActivity!(m[1])
      })
    }

    if (handlers.onTakeMedication) {
      registerCommand(/(?:médicament|insuline|pris mon|prendre)(?:\s+(.+))?/i, (m) => {
        handlers.onTakeMedication!(m[1])
      })
    }

    if (handlers.onHelp) {
      registerCommand(/(?:aide|help|comment|qu'est-ce que)/i, () => {
        handlers.onHelp!()
      })
    }
  }

  // Pre-defined commands in Darija
  function registerDarijaCommands(handlers: {
    onLogMeal?: (food?: string) => void
    onCheckGlucose?: () => void
    onLogActivity?: (activity?: string) => void
    onTakeMedication?: (medication?: string) => void
    onHelp?: () => void
  }) {
    if (handlers.onLogMeal) {
      registerCommand(/(?:كليت|فطرت|تعشيت|الماكلة)/i, (m) => {
        handlers.onLogMeal!(m[1])
      })
    }

    if (handlers.onCheckGlucose) {
      registerCommand(/(?:السكر|الگليكوز|شحال|كيفاش)/i, () => {
        handlers.onCheckGlucose!()
      })
    }

    if (handlers.onLogActivity) {
      registerCommand(/(?:رياضة|مشيت|درت سبور)/i, (m) => {
        handlers.onLogActivity!(m[1])
      })
    }

    if (handlers.onTakeMedication) {
      registerCommand(/(?:دوا|أنسولين|خديت)/i, (m) => {
        handlers.onTakeMedication!(m[1])
      })
    }

    if (handlers.onHelp) {
      registerCommand(/(?:عاوني|مساعدة|كيفاش ندير)/i, () => {
        handlers.onHelp!()
      })
    }
  }

  return {
    isListening,
    transcript,
    error,
    isSupported,
    startListening,
    processCommand,
    speak,
    registerCommand,
    registerDiabetesCommands,
    registerDarijaCommands,
  }
}
